import { cn } from '@/lib/utils';

/**
 * Skeletons, not spinners. Every one of these is shaped like the content it
 * replaces — same row height, same column widths — so the layout does not jump
 * when real data arrives.
 */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton', className)} aria-hidden />;
}

export function SkeletonText({
  lines = 1,
  className
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <div className={cn('space-y-2', className)} aria-hidden>
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="skeleton h-4"
          style={{ width: i === lines - 1 && lines > 1 ? '60%' : '100%' }}
        />
      ))}
    </div>
  );
}

/**
 * Table skeleton that mirrors a real table's column widths. Pass the same
 * widths array the header uses so columns don't shift on load.
 */
export function SkeletonTable({
  rows = 8,
  columns
}: {
  rows?: number;
  columns: { width: string; align?: 'left' | 'right' }[];
}) {
  return (
    <div role="status" aria-label="Loading" className="divide-y divide-[var(--line)]">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-4 px-4 py-3">
          {columns.map((c, i) => (
            <div key={i} className="shrink-0" style={{ width: c.width }}>
              <div
                className="skeleton h-4"
                style={{
                  width: `${55 + ((r * 7 + i * 13) % 40)}%`,
                  marginLeft: c.align === 'right' ? 'auto' : undefined
                }}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function SkeletonRows({ rows = 6, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('space-y-3', className)} role="status" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton h-14 rounded-xl" />
      ))}
    </div>
  );
}
